import React, { useState, useEffect } from 'react';
import { OpinionItemType, TokenStatus } from '../stores/data/types';
import { localStorageManager, STORAGE_KEYS } from '../utils/localStorage';
import { UserType } from '../stores/ui/uiStore';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { useToasterStore } from '../stores/ui/toasterStore';
import getApiErrorMessage from '../utils/getApiErrorMessage';

interface EvaluatorOpinionItem {
  type: OpinionItemType;
  point: number;
  comments: string;
}

interface EvaluatorOpinionTableProps {
  name: string;
  status: TokenStatus;
  items: EvaluatorOpinionItem[];
  totalScore: number;
  comment?: string;
  tokenAddress: string;
  existingOpinionNames?: string[]; // 기존에 존재하는 평가자 이름 목록
  onUpdate?: (items: EvaluatorOpinionItem[], comment?: string) => void;
  onSaveSuccess?: () => Promise<void> | void;
}

const EvaluatorOpinionTable: React.FC<EvaluatorOpinionTableProps> = ({
  name,
  items,
  status,
  totalScore,
  comment,
  tokenAddress,
  existingOpinionNames = [],
  onUpdate,
  onSaveSuccess,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState<EvaluatorOpinionItem[]>(items);
  const [editedComment, setEditedComment] = useState<string>(comment || '');
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { showToaster } = useToasterStore();

  // 로컬 스토리지에서 user 값 로드
  const loadUserFromStorage = () => {
    const storedUIState = localStorageManager.getItem<{ maxCardCount: number; user?: UserType }>(STORAGE_KEYS.UI_STATE);
    setCurrentUser(storedUIState?.user || null);
  };

  useEffect(() => {
    // 초기 로드
    loadUserFromStorage();

    // storage 이벤트 리스너 추가 (다른 탭에서 변경 시 반영)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.UI_STATE) {
        loadUserFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 커스텀 이벤트 리스너 추가 (같은 탭에서 변경 시 반영)
    const handleCustomStorageChange = () => {
      loadUserFromStorage();
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  // comment prop이 변경될 때 editedComment 업데이트 (편집 모드가 아닐 때만)
  useEffect(() => {
    if (!isEditing) {
      setEditedComment(comment || '');
    }
  }, [comment, isEditing]);

  // name과 currentUser가 일치하는지 확인
  const canEdit = currentUser !== null && name === currentUser;

  const handleStartEdit = () => {
    setEditedItems([...items]);
    setEditedComment(comment || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedItems([...items]);
    setEditedComment(comment || '');
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      // 기존에 해당 name의 평가자 의견이 존재하는지 확인
      const exists = existingOpinionNames.includes(name);

      // API 요청 형식으로 변환 (score는 point, comment는 comments)
      const apiItems = editedItems.map(item => ({
        type: item.type,
        score: item.point,
        comment: item.comments.trim() || undefined,
      }));

      if (exists) {
        // 10번 API: Update Evaluator Opinion
        await feManagementSystemApi.updateEvaluatorOpinion(tokenAddress, name, {
          items: apiItems,
          comment: editedComment.trim() || null,
        });
        showToaster('Evaluator opinion updated successfully', 'success');
      } else {
        // 9번 API: Add Evaluator Opinion
        await feManagementSystemApi.addEvaluatorOpinion(tokenAddress, {
          name,
          items: apiItems,
          comment: editedComment.trim() || undefined,
        });
        showToaster('Evaluator opinion added successfully', 'success');
      }

      // 로컬 상태 업데이트
      if (onUpdate) {
        onUpdate(editedItems, editedComment.trim() || undefined);
      }
      if (onSaveSuccess) {
        await onSaveSuccess();
      }
      setIsEditing(false);
    } catch (error: any) {
      console.error('Failed to save evaluator opinion:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to save evaluator opinion. Please try again.'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemChange = (index: number, field: 'point' | 'comments', value: string | number) => {
    const updated = [...editedItems];
    updated[index] = {
      ...updated[index],
      [field]: field === 'point' ? Number(value) : value,
    };
    setEditedItems(updated);
  };

  // 3점 척도에 따른 point 색상 반환
  const getPointStyle = (point: number, type?: string): string => {
    if (type === 'R') {
      switch (point) {
        case 0:
          return 'text-emerald-400';
        case 1:
          return 'text-yellow-400';
        case 2:
          return 'text-orange-400';
        case 3:
          return 'text-red-500';
        default:
          return 'text-gray-400';
      }
    }
    switch (point) {
      case 1:
        return 'text-red-500';
      case 2:
        return 'text-yellow-400';
      case 3:
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const getPointOptions = (type?: string) => {
    if (type === 'R') {
      return [
        { value: 0, label: '0' },
        { value: 1, label: '-1' },
        { value: 2, label: '-2' },
        { value: 3, label: '-3' },
      ];
    }
    return [
      { value: 0, label: '0' },
      { value: 1, label: '1' },
      { value: 2, label: '2' },
      { value: 3, label: '3' },
    ];
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h4 className="text-[18px] font-semibold text-white py-0">{name}</h4>
          <div className="h-4 w-px bg-gray-600"></div>
          <span className="text-base text-gray-400">Total Score: <span className="text-[18px] ml-1 text-white font-semibold">{totalScore}</span></span>
        </div>
        {canEdit && (
          !isEditing ? (
            <button
              onClick={handleStartEdit}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 transition-colors"
              title="편집"
            >
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
          ) : (
          <div className="flex gap-1">
            <button
              onClick={handleSaveEdit}
              disabled={isSaving}
              className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={isSaving}
              className="px-2 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
          )
        )}
      </div>

      {/* Comment 영역 */}
      {isEditing ? (
        <div className="mb-3">
          <textarea
            value={editedComment}
            onChange={(e) => setEditedComment(e.target.value)}
            placeholder="Enter final opinion..."
            className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            rows={3}
          />
        </div>
      ) : comment ? (
        <div className="mb-3">
          <div className="text-[16px] text-gray-300 border-gray-700 border-l-8 border-l-gray-700 pl-4 my-3" style={{ 
            lineHeight: 1.5, wordBreak: "break-all",
            overflowWrap: "break-word"
          }}>
            {comment}
          </div>
        </div>
      ) : null}

      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[12px] border-b border-gray-600">
              <th className="text-left py-2 pr-1 text-gray-300 font-medium w-12">Type</th>
              <th className="text-left py-2 px-0 text-gray-300 font-medium w-12">Point</th>
              <th className="text-left py-2 px-2 text-gray-300 font-medium">Comment</th>
            </tr>
          </thead>
          <tbody>
            {isEditing ? (
              editedItems.map((item, rowIndex) => (
                <tr key={rowIndex} className="text-[14px] border-b border-gray-700/50">
                  <td className="py-2 pr-1 text-white w-12">{item.type}</td>
                  <td className="py-2 px-0 w-12">
                    <select
                      value={item.point}
                      onChange={(e) => handleItemChange(rowIndex, 'point', Number(e.target.value))}
                      className={`w-full bg-gray-700 px-1 py-1 rounded text-xs border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 ${getPointStyle(item.point, item.type)}`}
                    >
                      {getPointOptions(item.type).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="text"
                      value={item.comments}
                      onChange={(e) => handleItemChange(rowIndex, 'comments', e.target.value)}
                      className="w-full bg-gray-700 text-white px-2 py-1 rounded text-xs border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder=""
                    />
                  </td>
                </tr>
              ))
            ) : (
              items.map((item, rowIndex) => (
                <tr key={rowIndex} className="text-[14px] border-b border-gray-700/50">
                  <td className="py-2 pr-1 text-white w-12">{item.type}</td>
                  <td className={`py-2 px-0 w-12 ${getPointStyle(item.point, item.type)}`}>
                    { item.type === 'R' ? -item.point : item.point}
                  </td>
                  <td className="py-2 px-2 text-gray-400">{item.comments}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EvaluatorOpinionTable;

